export type AnalyticsConsentChoice = "granted" | "denied";
export type AnalyticsPreferencesOpenDetail = {
  invoker: HTMLElement | null;
};

const CONSENT_KEY = "atlas:analytics-consent:v2";
// A GA4 measurement ID is a public routing identifier, not a secret.
export const ANALYTICS_MEASUREMENT_ID = import.meta.env?.VITE_GA_MEASUREMENT_ID?.trim() || "G-TNNCYKY95S";

type PageView = { location: string; title: string; referrer?: string };

export type AtlasAnalyticsEvent =
  | "quote_landing"
  | "quote_visible"
  | "next_step_click"
  | "reader_route_index_open"
  | "reader_route_start"
  | "reader_route_material_open"
  | "reader_route_practice_open"
  | "audio_start"
  | "audio_progress"
  | "audio_complete"
  | "audio_chapter_seek"
  | "audio_transcript_open"
  | "audio_download";

type AtlasAnalyticsEventParameters = Record<string, string | number | boolean>;

const X_QUOTE_HASH_PATTERN = /^#(atlas-x-\d{3})$/;

const ATTRIBUTION_PARAMETERS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

let analyticsStarted = false;
let pendingPageView: PageView | undefined;
let currentPageView: PageView | undefined;
let lastPageLocation: string | undefined;
let activeConsentChoice: AnalyticsConsentChoice | null | undefined;
const trackedOnceEvents = new Set<string>();

function isProductionAtlas() {
  return window.location.hostname === "atlas.synergylm.com";
}

function gtag(command: string, ...parameters: unknown[]): void;
function gtag() {
  window.dataLayer ??= [];
  // gtag.js expects the native Arguments object used by Google's canonical snippet.
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

function cleanLocation(value: string) {
  const url = new URL(value, window.location.origin);
  const attribution = new URLSearchParams();
  for (const name of ATTRIBUTION_PARAMETERS) {
    const campaignValue = url.searchParams.get(name);
    if (campaignValue) attribution.set(name, campaignValue);
  }
  const query = attribution.toString();
  return `${url.origin}${url.pathname}${query ? `?${query}` : ""}`;
}

function cleanReferrer(value: string) {
  if (!value) return "";
  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return "";
  }
}

function setPrivacySafePageContext(location = window.location.href, referrer = document.referrer) {
  gtag("set", {
    page_location: cleanLocation(location),
    page_referrer: cleanReferrer(referrer),
  });
}

function sendPageView(pageView: PageView, force = false) {
  if (!analyticsStarted) return;
  const location = cleanLocation(pageView.location);
  const referrer = pageView.referrer
    ?? (location === currentPageView?.location ? currentPageView.referrer : undefined)
    ?? lastPageLocation
    ?? cleanReferrer(document.referrer);
  currentPageView = { ...pageView, location, referrer };
  if (!force && location === lastPageLocation) return;
  setPrivacySafePageContext(location, referrer);
  gtag("event", "page_view", {
    page_location: location,
    page_title: pageView.title,
    page_referrer: referrer,
  });
  lastPageLocation = location;
}

function flushPageView() {
  if (!pendingPageView || !analyticsStarted) return;
  const pageView = pendingPageView;
  pendingPageView = undefined;
  sendPageView(pageView);
}

function startAnalytics(initialChoice: AnalyticsConsentChoice | null = getAnalyticsConsent()) {
  if (analyticsStarted || !isProductionAtlas() || !ANALYTICS_MEASUREMENT_ID) return;
  analyticsStarted = true;
  activeConsentChoice = initialChoice;

  gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  gtag("set", "ads_data_redaction", true);
  setPrivacySafePageContext();
  if (initialChoice === "granted") {
    gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }
  gtag("js", new Date());
  gtag("config", ANALYTICS_MEASUREMENT_ID, {
    send_page_view: false,
    allow_google_signals: false,
    allow_ad_personalization_signals: false,
    page_location: cleanLocation(window.location.href),
    page_referrer: cleanReferrer(document.referrer),
  });

  if (!document.getElementById("atlas-google-analytics")) {
    const script = document.createElement("script");
    script.id = "atlas-google-analytics";
    script.async = true;
    script.referrerPolicy = "origin";
    script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(ANALYTICS_MEASUREMENT_ID)}`;
    document.head.appendChild(script);
  }
  flushPageView();
}

function expireAnalyticsCookies() {
  const names = ["_ga", `_ga_${ANALYTICS_MEASUREMENT_ID.replace(/^G-/, "")}`];
  for (const name of names) {
    for (const domain of ["", "; domain=atlas.synergylm.com", "; domain=.synergylm.com"]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}; SameSite=Lax`;
    }
  }
}

export function analyticsIsAvailable() {
  return isProductionAtlas() && Boolean(ANALYTICS_MEASUREMENT_ID);
}

export function getAnalyticsConsent(): AnalyticsConsentChoice | null {
  try {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function initializeAnalytics() {
  if (!analyticsIsAvailable()) return;
  const choice = getAnalyticsConsent();
  if (choice !== "granted") expireAnalyticsCookies();
  startAnalytics(choice);
}

export function setAnalyticsConsent(choice: AnalyticsConsentChoice) {
  const previousChoice = activeConsentChoice === undefined ? getAnalyticsConsent() : activeConsentChoice;
  const analyticsWasAlreadyStarted = analyticsStarted;
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    // A blocked storage API must never block reading the Atlas.
  }

  startAnalytics(choice);
  setPrivacySafePageContext();
  if (choice === "granted") {
    gtag("consent", "update", {
      analytics_storage: "granted",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    activeConsentChoice = "granted";
    if (analyticsWasAlreadyStarted && previousChoice !== "granted" && currentPageView) {
      sendPageView(currentPageView, true);
    } else {
      flushPageView();
    }
  } else {
    gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    activeConsentChoice = "denied";
    expireAnalyticsCookies();
  }
  window.dispatchEvent(new CustomEvent("atlas:analytics-consent-change", { detail: choice }));
}

export function openAnalyticsPreferences(invoker: HTMLElement | null = null) {
  window.dispatchEvent(new CustomEvent<AnalyticsPreferencesOpenDetail>(
    "atlas:analytics-preferences-open",
    { detail: { invoker } },
  ));
}

export function trackPageView(location: string, title: string) {
  pendingPageView = { location: cleanLocation(location), title };
  startAnalytics();
  flushPageView();
}

export function getXQuoteIdFromHash(hash: string): string | null {
  return hash.match(X_QUOTE_HASH_PATTERN)?.[1] ?? null;
}

export function trackAtlasEvent(name: AtlasAnalyticsEvent, parameters: AtlasAnalyticsEventParameters = {}) {
  startAnalytics();
  if (!analyticsStarted || activeConsentChoice !== "granted") return false;
  setPrivacySafePageContext();
  gtag("event", name, parameters);
  return true;
}

export function trackAtlasEventOnce(
  name: AtlasAnalyticsEvent,
  deduplicationKey: string,
  parameters: AtlasAnalyticsEventParameters = {},
) {
  const key = `${name}:${deduplicationKey}`;
  if (trackedOnceEvents.has(key)) return false;
  const tracked = trackAtlasEvent(name, parameters);
  if (tracked) trackedOnceEvents.add(key);
  return tracked;
}
